import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, Link, TextField, Typography } from '@mui/material'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { useIdsFromPastedString, useTreeIdRouting } from './navigationUtilities'
import { allProductsById } from 'shared/api/masters'
import { ProductSelection } from '../products/ProductSelection'
import { useGetTreeById } from 'shared/hooks/trees/hooks'
import { createIdMap } from '../../version_mapping/utility'
import { directTreeAccessUrl } from 'infrastructure/api/kiddomPaths'

export function TreeNodeRouter() {
  const [fromMasters, setFromMasters] = useState(false)
  const routingFunc = useTreeIdRouting()
  return (
    <Box>
      <TreeIdInput onChange={routingFunc} />
      <Button onClick={() => setFromMasters(true)}>Editable Trees</Button>
      {fromMasters && <EditableTreeSelect onSelect={routingFunc} />}
    </Box>
  )
}

export function EditableTreeSelect({
  onSelect,
}: {
  onSelect: (ids: { treeId: string; version: string }) => void
}) {
  const allProducts = useAtomValue(allProductsById)

  if (!allProducts || !Object.keys(allProducts).length) {
    return <Box>Loading...</Box>
  }

  const doSelect = ({ id }: { id: number }) => {
    const product = allProducts[id]
    if (!product?.editable_tree) return
    const { tree_id: treeId, version_id: version } = product.editable_tree
    return onSelect({ treeId, version })
  }

  return (
    <Box width="340px">
      <ProductSelection onSelect={doSelect} />
    </Box>
  )
}

type TreeIdInputBaseProps = {
  cta?: string
  canSelectTree?: (ids: { treeId: string; versionId: string }) => boolean
}

type TreeIdInputNavigateProps = TreeIdInputBaseProps & {
  onChange: (ids: {
    treeId: string
    version: string
    versionId: string
    nodeId?: string
    title: string
  }) => void
  onSelect?: never
  fetchTrigger?: 'input'
  showErrors?: never
}

export type TreeSelectResult = {
  treeId: string
  treeVer: string
  title: string
}

type TreeIdInputSelectProps = TreeIdInputBaseProps & {
  onChange?: never
  onSelect: (ids: TreeSelectResult | null) => void
  fetchTrigger: 'button'
  showErrors?: boolean
}

type TreeIdInputProps = TreeIdInputNavigateProps | TreeIdInputSelectProps

export function TreeIdInput({
  onChange,
  onSelect,
  cta = 'Go',
  canSelectTree = () => true,
  fetchTrigger = 'input',
  showErrors = false,
}: TreeIdInputProps) {
  const [currentIdValue, setCurrentIdValue] = useState('')
  const [treeIds, setTreeIds] = useState<{
    treeId?: string | undefined
    versionId?: string | undefined
    nodeId?: string
  }>({})
  const [lookupError, setLookupError] = useState<
    'invalid_format' | 'not_found' | null
  >(null)
  const [findTrigger, setFindTrigger] = useState(0)
  const getIds = useIdsFromPastedString()
  const { tree, retrieving } = useGetTreeById(treeIds)
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  const isSelectMode = fetchTrigger === 'button' && onSelect

  useEffect(() => {
    if (!isSelectMode) {
      const { treeId, version, nodeId } = getIds(currentIdValue)
      setTreeIds({ treeId, versionId: version, nodeId })
    }
  }, [currentIdValue, getIds, isSelectMode])

  useEffect(() => {
    if (!tree) return
    if (!treeIds.nodeId) return
    const { nodeId } = treeIds
    const mapping = createIdMap(tree)
    if (nodeId in mapping) {
      return
    }
    setTreeIds((prev) => ({ ...prev, nodeId: undefined }))
  }, [tree, treeIds.nodeId])

  useEffect(() => {
    if (!isSelectMode || retrieving || !treeIds.treeId || !treeIds.versionId)
      return
    if (tree) {
      setLookupError(null)
      onSelectRef.current?.({
        treeId: tree.tree_id,
        treeVer: tree.version,
        title: tree.title ?? '',
      })
    } else {
      setLookupError('not_found')
      onSelectRef.current?.(null)
    }
  }, [
    isSelectMode,
    tree,
    retrieving,
    treeIds.treeId,
    treeIds.versionId,
    findTrigger,
  ])

  const handleFindTree = () => {
    setLookupError(null)
    onSelectRef.current?.(null)
    const { treeId, version } = getIds(currentIdValue)
    if (!treeId || !version) {
      setTreeIds({})
      setLookupError('invalid_format')
      return
    }
    setTreeIds({ treeId, versionId: version })
    setFindTrigger((t) => t + 1)
  }

  const handleInputChange = (value: string) => {
    setCurrentIdValue(value)
    if (isSelectMode) {
      setTreeIds({})
      setLookupError(null)
      onSelectRef.current?.(null)
    }
  }

  return (
    <Box>
      {isSelectMode ? (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Tree
        </Typography>
      ) : (
        'Paste treeId:versionId:nodeId or URL here to go to that tree node'
      )}
      <Box padding={isSelectMode ? 0 : '5px'}>
        <TextField
          label="TreeId or URL"
          fullWidth
          size={isSelectMode ? 'small' : undefined}
          value={currentIdValue}
          onChange={(e) =>
            isSelectMode
              ? handleInputChange(e.target.value)
              : setCurrentIdValue(e.target.value)
          }
          placeholder={
            isSelectMode
              ? 'Paste treeId:versionId or curriculum URL'
              : undefined
          }
          sx={isSelectMode ? { mb: 1 } : undefined}
        />
      </Box>
      {isSelectMode && (
        <Button
          variant="outlined"
          onClick={handleFindTree}
          disabled={retrieving || currentIdValue.trim() === ''}
        >
          {retrieving ? 'Loading...' : 'Find Tree'}
        </Button>
      )}
      {!isSelectMode && retrieving && <Box>Loading...</Box>}
      {showErrors && lookupError === 'invalid_format' && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          Invalid format: paste treeId:versionId or a curriculum URL
        </Typography>
      )}
      {showErrors && lookupError === 'not_found' && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          Tree not found
        </Typography>
      )}
      {tree && isSelectMode && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 0.5,
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontWeight: 500,
            }}
          >
            {tree.title}
          </Typography>
          <Link
            href={directTreeAccessUrl({
              treeId: tree.tree_id,
              versionId: tree.version,
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            {directTreeAccessUrl({
              treeId: tree.tree_id,
              versionId: tree.version,
            })}
          </Link>
        </Box>
      )}
      {tree && !isSelectMode && (
        <Box>
          {tree.title}
          <Button
            disabled={
              !canSelectTree({ treeId: tree.tree_id, versionId: tree.version })
            }
            onClick={() =>
              onChange?.({
                treeId: tree.tree_id,
                version: tree.version,
                versionId: tree.version,
                title: tree.title,
                nodeId: treeIds.nodeId,
              })
            }
          >
            {cta}
          </Button>
        </Box>
      )}
    </Box>
  )
}
